// ============================================================================
// React Query Client Configuration
// ============================================================================
// Centralized configuration for TanStack Query (React Query)
// - Caching strategy
// - Retry logic
// - Stale time configuration
// - Error handling defaults
// ============================================================================

import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query';
import { reportError } from './errorReporter';

/**
 * Default query options applied to all queries
 */
const defaultQueryOptions = {
  queries: {
    // Data considered fresh for 1 minute
    // During this time, no refetch will occur
    staleTime: 60 * 1000, // 1 minute

    // Data remains in cache for 5 minutes after becoming unused
    // This allows instant loading when navigating back
    gcTime: 5 * 60 * 1000, // 5 minutes (formerly cacheTime)

    // Retry failed requests 3 times
    retry: 3,

    // Exponential backoff for retries
    // Attempt 1: 1s, Attempt 2: 2s, Attempt 3: 4s
    retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),

    // Don't refetch on window focus — dashboards are driven by explicit period/brand
    // selectors and an alt-tab shouldn't refire ~10 Snowflake queries.
    refetchOnWindowFocus: false,

    // Don't refetch on component mount if data is fresh
    refetchOnMount: true,

    // Refetch on network reconnection
    refetchOnReconnect: true,

    // Don't refetch interval by default
    // Individual queries can override this
    refetchInterval: false,

    // Throw errors to error boundaries
    // This allows centralized error handling
    throwOnError: false,

    // Don't retry on certain status codes
    retryOnMount: true,

    // Network mode - fail when offline
    networkMode: 'online' as const,
  },
  mutations: {
    // Retry mutations once on failure
    retry: 1,

    // Don't retry on certain errors
    retryDelay: 1000,

    // Network mode for mutations
    networkMode: 'online' as const,

    // Throw mutation errors to error boundaries
    throwOnError: false,
  },
};

/**
 * Create and configure the QueryClient instance
 * QueryCache/MutationCache onError hooks send non-auth errors to Sentry.
 */
export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError(error, query) {
      reportError(error, `Query failed: ${query.queryHash}`, {
        source: 'react-query',
        context: { queryKey: query.queryKey },
      });
    },
  }),
  mutationCache: new MutationCache({
    onError(error, _variables, _context, mutation) {
      reportError(error, 'Mutation failed', {
        source: 'react-query',
        context: { mutationKey: mutation.options.mutationKey },
      });
    },
  }),
  defaultOptions: defaultQueryOptions,
});

/**
 * Query key factory for consistent cache keys
 * This helps with cache invalidation and data refetching
 */
export const queryKeys = {
  // Portal / Intelligence Suite
  portal: {
    all: ['portal'] as const,
    styles: (filters?: unknown) => [...queryKeys.portal.all, 'styles', filters] as const,
    style: (id: string) => [...queryKeys.portal.all, 'style', id] as const,
    styleMetrics: (id: string, timePeriod?: string) =>
      [...queryKeys.portal.style(id), 'metrics', timePeriod] as const,
    styleTrends: (id: string, dateRange?: unknown) =>
      [...queryKeys.portal.style(id), 'trends', dateRange] as const,
    styleMarketplaces: (id: string, dateRange?: unknown) =>
      [...queryKeys.portal.style(id), 'marketplaces', dateRange] as const,
    dashboard: (dateRange?: unknown) =>
      [...queryKeys.portal.all, 'dashboard', dateRange] as const,
  },

  // Catalog
  catalog: {
    all: ['catalog'] as const,
    products: (filters?: unknown) =>
      [...queryKeys.catalog.all, 'products', filters] as const,
    product: (id: string) => [...queryKeys.catalog.all, 'product', id] as const,
    productBySku: (sku: string) =>
      [...queryKeys.catalog.all, 'product', 'sku', sku] as const,
    categories: () => [...queryKeys.catalog.all, 'categories'] as const,
    subcategories: (category: string) =>
      [...queryKeys.catalog.all, 'subcategories', category] as const,
    brands: () => [...queryKeys.catalog.all, 'brands'] as const,
    marketplaces: () => [...queryKeys.catalog.all, 'marketplaces'] as const,
  },

  // Sales
  sales: {
    all: ['sales'] as const,
    metrics: (filters?: unknown) => [...queryKeys.sales.all, 'metrics', filters] as const,
    byChannel: (filters?: unknown) =>
      [...queryKeys.sales.all, 'by-channel', filters] as const,
    trend: (filters?: unknown) => [...queryKeys.sales.all, 'trend', filters] as const,
    byGeography: (filters?: unknown) =>
      [...queryKeys.sales.all, 'by-geography', filters] as const,
    topProducts: (filters?: unknown, limit?: number) =>
      [...queryKeys.sales.all, 'top-products', filters, limit] as const,
    byCategory: (filters?: unknown) =>
      [...queryKeys.sales.all, 'by-category', filters] as const,
    byBrand: (filters?: unknown) => [...queryKeys.sales.all, 'by-brand', filters] as const,
  },

  // Promotions
  promotions: {
    all: ['promotions'] as const,
    campaigns: (filters?: unknown) =>
      [...queryKeys.promotions.all, 'campaigns', filters] as const,
    campaign: (id: string) => [...queryKeys.promotions.all, 'campaign', id] as const,
    campaignPerformance: (id: string) =>
      [...queryKeys.promotions.campaign(id), 'performance'] as const,
    marketplaceOpportunities: (filters?: unknown) =>
      [...queryKeys.promotions.all, 'marketplace-opportunities', filters] as const,
    marketplaceOpportunity: (id: string) =>
      [...queryKeys.promotions.all, 'marketplace-opportunity', id] as const,
    channelPerformance: () =>
      [...queryKeys.promotions.all, 'channel-performance'] as const,
    topProducts: (limit?: number) =>
      [...queryKeys.promotions.all, 'top-products', limit] as const,
  },

  // Reconciliation
  reconcile: {
    all: ['reconcile'] as const,
    reports: (filters?: unknown) =>
      [...queryKeys.reconcile.all, 'reports', filters] as const,
    report: (id: string) => [...queryKeys.reconcile.all, 'report', id] as const,
    discrepancies: (reportId: string, filters?: unknown) =>
      [...queryKeys.reconcile.report(reportId), 'discrepancies', filters] as const,
    discrepancy: (id: string) => [...queryKeys.reconcile.all, 'discrepancy', id] as const,
    summary: (filters?: unknown) =>
      [...queryKeys.reconcile.all, 'summary', filters] as const,
  },

  // Auth
  auth: {
    all: ['auth'] as const,
    user: () => [...queryKeys.auth.all, 'user'] as const,
  },

  // Admin / Executive Dashboard
  admin: {
    all: ['admin'] as const,
    dashboard: () => [...queryKeys.admin.all, 'dashboard'] as const,
    inventory: () => [...queryKeys.admin.all, 'inventory'] as const,
    brands: () => [...queryKeys.admin.all, 'brands'] as const,
    brandDetail: (brandId: number) =>
      [...queryKeys.admin.all, 'brand', brandId] as const,
  },
} as const;

/**
 * Helper function to invalidate all queries for a specific domain
 */
export const invalidateQueries = {
  portal: () => queryClient.invalidateQueries({ queryKey: queryKeys.portal.all }),
  catalog: () => queryClient.invalidateQueries({ queryKey: queryKeys.catalog.all }),
  sales: () => queryClient.invalidateQueries({ queryKey: queryKeys.sales.all }),
  promotions: () => queryClient.invalidateQueries({ queryKey: queryKeys.promotions.all }),
  reconcile: () => queryClient.invalidateQueries({ queryKey: queryKeys.reconcile.all }),
  auth: () => queryClient.invalidateQueries({ queryKey: queryKeys.auth.all }),
  admin: () => queryClient.invalidateQueries({ queryKey: queryKeys.admin.all }),
  all: () => queryClient.invalidateQueries(),
};

export default queryClient;
